import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { useState, useEffect } from 'react'
import { Link, useRoute, useLocation } from 'wouter'
import { ArrowLeft, Send, DollarSign, Calendar, User, Building, Sparkles, Crown, AlertCircle } from 'lucide-react'
import { SubscriptionModal } from '@/components/subscription-modal'
import { AIAssistant } from '@/components/ai-assistant'

import toast from 'react-hot-toast'
import { Rfp, CreateResponseData } from '@/types'
import { buildApiUrl } from '@/config/api'

export function RespondToRfp() {

  const [, setLocation] = useLocation()
  const [, params] = useRoute('/respond/:rfpId')
  const rfpId = params?.rfpId
  
  const [rfp, setRfp] = useState<Rfp | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false)
  const [showAIAssistant, setShowAIAssistant] = useState(false)

  // Subscription status
  const [subscriptionStatus, setSubscriptionStatus] = useState<{
    hasActiveSubscription: boolean
    subscriptionStatus: string
    subscriptionPlan: string
  } | null>(null)
  const [isCheckingSubscription, setIsCheckingSubscription] = useState(true)

  const [formData, setFormData] = useState({
    message: '',
    proposal: '',
    budget: '',
    timeline: '',
    attachments: [] as string[],
  })

  // Check subscription status on mount
  useEffect(() => {
    const checkSubscription = async () => {
      try {
        const token = localStorage.getItem('token')
        if (!token) {
          setIsCheckingSubscription(false)
          return
        }
        const resp = await fetch(buildApiUrl('/api/subscription/status'), {
          headers: { 'Authorization': `Bearer ${token}` },
        })
        if (resp.ok) {
          const data = await resp.json()
          setSubscriptionStatus(data.data)
        }
      } catch (e) {
        console.error('Subscription status check failed:', e)
      } finally {
        setIsCheckingSubscription(false)
      }
    }
    checkSubscription()
  }, [])

  // Fetch RFP data
  useEffect(() => {
    const fetchRfp = async () => {
      if (!rfpId) {
        setError('RFP ID is required')
        setIsLoading(false)
        return
      }

      try {
        const token = localStorage.getItem('token')
        if (!token) {
          toast.error('Please log in to respond to RFPs')
          setLocation('/login')
          return
        }

        const response = await fetch(buildApiUrl(`/api/rfps/${rfpId}`), {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        })

        if (response.ok) {
          const data = await response.json()
          setRfp(data.data)
        } else if (response.status === 404) {
          setError('RFP not found')
        } else {
          setError('Failed to load RFP')
        }
      } catch (error) {
        console.error('Error fetching RFP:', error)
        setError('Failed to load RFP')
      } finally {
        setIsLoading(false)
      }
    }

    fetchRfp()
  }, [rfpId, setLocation])

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }))
  }

  const handleAIContentGenerated = (content: string) => {
    // Parse the AI-generated content and update form fields
    const lines = content.split('\n')
    let currentSection = ''
    let proposal = ''
    let message = ''
    
    for (const line of lines) {
      const trimmedLine = line.trim()
      if (!trimmedLine) continue
      
      // Detect sections
      if (trimmedLine.toLowerCase().includes('executive summary') || 
          trimmedLine.toLowerCase().includes('company profile')) {
        currentSection = 'message'
        continue
      }
      
      if (trimmedLine.toLowerCase().includes('proposed solution') ||
          trimmedLine.toLowerCase().includes('technical implementation')) {
        currentSection = 'proposal'
        continue
      }
      
      // Extract content based on section
      if (currentSection === 'message' && trimmedLine.length > 10) {
        message += (message ? '\n' : '') + trimmedLine
      } else if (currentSection === 'proposal' && trimmedLine.length > 10) {
        proposal += (proposal ? '\n' : '') + trimmedLine
      }
    }
    
    setFormData(prev => ({
      ...prev,
      message: message || prev.message,
      proposal: proposal || prev.proposal
    }))
    
    setShowAIAssistant(false)
    toast.success('AI content applied to form!')
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsSubmitting(true)

    try {
      // Validate form data
      if (!formData.message.trim() || formData.message.length < 10) {
        toast.error('Message must be at least 10 characters long')
        return
      }
      if (!formData.proposal.trim() || formData.proposal.length < 50) {
        toast.error('Proposal must be at least 50 characters long')
        return
      }
      if (!formData.budget || parseFloat(formData.budget) <= 0) {
        toast.error('Budget must be greater than 0')
        return
      }
      if (!formData.timeline.trim()) {
        toast.error('Timeline is required')
        return
      }

      const token = localStorage.getItem('token')
      if (!token) {
        toast.error('Please log in to respond to RFPs')
        return
      }

      const responseData: CreateResponseData = {
        rfpId: rfpId!,
        message: formData.message.trim(),
        proposal: formData.proposal.trim(),
        budget: parseFloat(formData.budget),
        timeline: formData.timeline.trim(),
        attachments: formData.attachments,
      }

      const response = await fetch(buildApiUrl('/api/responses'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(responseData),
      })

      if (response.ok) {
        toast.success('Response submitted successfully!')
        setLocation(`/rfp/${rfpId}`)
      } else {
        const errorData = await response.json()
        if (errorData.error === 'Paid subscription required' || response.status === 403) {
          setShowSubscriptionModal(true)
        } else {
          toast.error(errorData.error || 'Failed to submit response')
        }
      }
    } catch (error) {
      console.error('Error submitting response:', error)
      toast.error('Failed to submit response')
    } finally {
      setIsSubmitting(false)
    }
  }

  if (isLoading) {
    return (
      <div className="space-y-8">
        <div className="flex items-center space-x-4">
          <Link href="/request-for-proposal-marketplace">
            <Button variant="ghost" size="icon">
              <ArrowLeft className="h-4 w-4" />
            </Button>
          </Link>
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Loading RFP...</h1>
          </div>
        </div>
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
            <p className="mt-2 text-muted-foreground">Loading RFP details...</p>
          </div>
        </div>
      </div>
    )
  }

  if (error || !rfp) {
    return (
      <div className="space-y-8">
        <div className="flex items-center space-x-4">
          <Link href="/request-for-proposal-marketplace">
            <Button variant="ghost" size="icon">
              <ArrowLeft className="h-4 w-4" />
            </Button>
          </Link>
          <div>
            <h1 className="text-3xl font-bold tracking-tight">RFP Not Found</h1>
            <p className="text-muted-foreground">
              {error || 'The RFP you are looking for does not exist or you do not have access to it.'}
            </p>
          </div>
        </div>
        <div className="flex items-center justify-center py-12">
          <div className="text-center">
            <p className="text-muted-foreground mb-4">
              {error || 'RFP not found'}
            </p>
            <Link href="/request-for-proposal-marketplace">
              <Button>
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back to RFPs
              </Button>
            </Link>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center space-x-4">
        <Link href={`/rfp/${rfpId}`}>
          <Button variant="ghost" size="icon">
            <ArrowLeft className="h-4 w-4" />
          </Button>
        </Link>
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Respond to RFP</h1>
          <p className="text-muted-foreground">
            Submit your proposal for: {rfp.title}
          </p>
        </div>
      </div>

      {/* Subscription banners */}
      {isCheckingSubscription && (
        <Card className="border-blue-200 bg-blue-50">
          <CardContent className="pt-6">
            <div className="flex items-center space-x-3">
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
              <p className="text-blue-700 text-sm">Checking subscription status...</p>
            </div>
          </CardContent>
        </Card>
      )}

      {!isCheckingSubscription && subscriptionStatus && !subscriptionStatus.hasActiveSubscription && (
        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="pt-6">
            <div className="flex items-start space-x-3">
              <AlertCircle className="h-5 w-5 text-amber-600 mt-0.5" />
              <div className="flex-1">
                <h3 className="font-semibold text-amber-800 mb-2">PRO Subscription Required</h3>
                <p className="text-amber-700 text-sm mb-3">
                  Responding to RFPs requires a PRO subscription. You currently have a {subscriptionStatus.subscriptionStatus} plan.
                </p>
                <Button onClick={() => setShowSubscriptionModal(true)} className="bg-amber-600 hover:bg-amber-700 text-white">
                  <Crown className="mr-2 h-4 w-4" />
                  Upgrade to PRO
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="relative">
        {/* Disable form if no active subscription */}
        {!isCheckingSubscription && subscriptionStatus && !subscriptionStatus.hasActiveSubscription && (
          <div className="absolute inset-0 bg-white/50 backdrop-blur-sm z-10 rounded-lg flex items-center justify-center">
            <div className="text-center">
              <Crown className="h-12 w-12 text-amber-600 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">PRO Subscription Required</h3>
              <p className="text-gray-600 mb-4">Please upgrade to PRO to respond to RFPs</p>
              <Button onClick={() => setShowSubscriptionModal(true)} className="bg-amber-600 hover:bg-amber-700 text-white">
                <Crown className="mr-2 h-4 w-4" />
                Upgrade to PRO
              </Button>
            </div>
          </div>
        )}

        <div
          className={
            !isCheckingSubscription &&
            subscriptionStatus &&
            !subscriptionStatus.hasActiveSubscription
              ? 'pointer-events-none opacity-50'
              : ''
          }
        >
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* RFP Details */}
            <div className="lg:col-span-1">
              <Card>
                <CardHeader>
                  <CardTitle>RFP Details</CardTitle>
                  <CardDescription>
                    Information about the RFP you're responding to.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <h3 className="font-medium mb-2">{rfp.title}</h3>
                    <p className="text-sm text-muted-foreground">{rfp.description}</p>
                  </div>
                  
                  <div className="space-y-3">
                    <div className="flex items-center space-x-2">
                      <User className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm text-muted-foreground">Posted by:</span>
                      <span className="text-sm font-medium">{rfp.user?.name || 'Unknown'}</span>
                    </div>
                    
                    <div className="flex items-center space-x-2">
                      <Building className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm text-muted-foreground">Company:</span>
                      <span className="text-sm font-medium">{rfp.user?.company || 'Unknown'}</span>
                    </div>
                    
                    <div className="flex items-center space-x-2">
                      <DollarSign className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm text-muted-foreground">Budget:</span>
                      <span className="text-sm font-medium">
                        ${rfp.budgetMin?.toLocaleString() || 0} - ${rfp.budgetMax?.toLocaleString() || 0} {rfp.budgetCurrency || 'USD'}
                      </span>
                    </div>
                    
                    <div className="flex items-center space-x-2">
                      <Calendar className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm text-muted-foreground">Deadline:</span>
                      <span className="text-sm font-medium">
                        {new Date(rfp.deadline).toLocaleDateString()}
                      </span>
                    </div>
                  </div>

                  <div>
                    <h4 className="font-medium mb-2">Requirements</h4>
                    <ul className="text-sm text-muted-foreground space-y-1">
                      {rfp.requirements.map((req, index) => (
                        <li key={index} className="flex items-start space-x-2">
                          <span className="flex-shrink-0 w-1.5 h-1.5 bg-primary rounded-full mt-2"></span>
                          <span>{req}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Response Form */}
            <div className="lg:col-span-2">
              <Card>
                <CardHeader>
                  <CardTitle>Your Proposal</CardTitle>
                  <CardDescription>
                    Provide a detailed response to this RFP. Be specific about your approach, timeline, and pricing.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleSubmit} className="space-y-6">
                    {/* Message */}
                    <div>
                      <label htmlFor="message" className="block text-sm font-medium mb-2">
                        Initial Message
                      </label>
                      <div className="flex space-x-2">
                        <textarea
                          id="message"
                          value={formData.message}
                          onChange={(e) => handleInputChange('message', e.target.value)}
                          placeholder="Brief introduction and overview of your approach..."
                          className="flex-1 p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary focus:border-transparent"
                          rows={3}
                          required
                        />
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => setShowAIAssistant(true)}
                        >
                          <Sparkles className="h-4 w-4" />
                        </Button>
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">
                        Minimum 10 characters. This will be the first thing the RFP owner sees.
                      </p>
                    </div>

                    {/* Detailed Proposal */}
                    <div>
                      <label htmlFor="proposal" className="block text-sm font-medium mb-2">
                        Detailed Proposal
                      </label>
                      <textarea
                        id="proposal"
                        value={formData.proposal}
                        onChange={(e) => handleInputChange('proposal', e.target.value)}
                        placeholder="Provide a comprehensive proposal including your methodology, deliverables, team composition, and any other relevant details..."
                        className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary focus:border-transparent"
                        rows={8}
                        required
                      />
                      <p className="text-xs text-muted-foreground mt-1">
                        Minimum 50 characters. Be detailed and specific about your approach.
                      </p>
                    </div>

                    {/* Budget and Timeline */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="budget" className="block text-sm font-medium mb-2">
                          Your Proposed Budget ({rfp.budgetCurrency})
                        </label>
                        <input
                          type="number"
                          id="budget"
                          value={formData.budget}
                          onChange={(e) => handleInputChange('budget', e.target.value)}
                          placeholder="Enter your proposed budget"
                          className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary focus:border-transparent"
                          min="0"
                          step="0.01"
                          required
                        />
                      </div>
                      
                      <div>
                        <label htmlFor="timeline" className="block text-sm font-medium mb-2">
                          Estimated Timeline
                        </label>
                        <input
                          type="text"
                          id="timeline"
                          value={formData.timeline}
                          onChange={(e) => handleInputChange('timeline', e.target.value)}
                          placeholder="e.g., 3-4 weeks, 2 months, etc."
                          className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary focus:border-transparent"
                          required
                        />
                      </div>
                    </div>

                    {/* Submit Button */}
                    <div className="flex justify-end space-x-4">
                      <Link href={`/rfp/${rfpId}`}>
                        <Button type="button" variant="outline">
                          Cancel
                        </Button>
                      </Link>
                      <Button type="submit" disabled={isSubmitting}>
                        {isSubmitting ? (
                          <>
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                            Submitting...
                          </>
                        ) : (
                          <>
                            <Send className="mr-2 h-4 w-4" />
                            Submit Response
                          </>
                        )}
                      </Button>
                    </div>
                  </form>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>

      {/* AI Assistant Modal */}
      {showAIAssistant && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold">AI Assistant</h2>
                <Button
                  variant="ghost"
                  onClick={() => setShowAIAssistant(false)}
                >
                  ×
                </Button>
              </div>
              <AIAssistant
                type="response"
                onContentGenerated={handleAIContentGenerated}
                initialData={{
                  rfpTitle: rfp?.title || '',
                  rfpDescription: rfp?.description || '',
                  rfpRequirements: (rfp?.requirements || []).join('\n'),
                  companyName: rfp?.user?.company || 'Your Company',
                  companyDescription: '',
                  proposedSolution: formData.proposal,
                  pricing: formData.budget ? `${rfp?.budgetCurrency || 'USD'} ${formData.budget}` : undefined,
                  timeline: formData.timeline,
                }}
              />
            </div>
          </div>
        </div>
      )}

      <SubscriptionModal
        isOpen={showSubscriptionModal}
        onClose={() => setShowSubscriptionModal(false)}
        feature="respond"
        currentPlan={subscriptionStatus?.subscriptionPlan as 'FREE' | 'PRO'}
        hasActiveSubscription={subscriptionStatus?.hasActiveSubscription || false}
      />
    </div>
  )
}